import { FC } from 'react'
import { SectionStepsProps } from '@portfolio/models'
import { styled } from '@portfolio/styles'
import { PortableText, SectionWrapper } from '@portfolio/components'

const Container = styled('div', {
  margin: '$24 $0',
  marginTop: 0,
  gap: '68px',
  display: 'flex',
  flexDirection: 'column',

  '@lg': {
    margin: '$24 $30',
    marginTop: 0,
    gap: '$64',
  },

  '@xxl': {
    maxWidth: '$maxWidthDesktop',
    margin: '0 auto',
  },
})

const Title = styled('h2', {
  projectFont: 'heading04',
  color: '$white',
  fontSize: '34px',
  lineHeight: '110%',
  letterSpacing: '-1.02px',
  padding: '$0 $24',
  height: '37px',

  '@lg': {
    fontSize: '36px',
    letterSpacing: '-1.08px',
    height: '$40',
    padding: '$0',
  },
})

const StepsScrollContainer = styled('div', {
  display: 'flex',
  gap: '$20',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollSnapType: 'x mandatory',
  padding: '$0 $20',
  scrollPadding: '$0 $20',

  '&::-webkit-scrollbar': {
    display: 'none',
  },

  '@lg': {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '$20',
    overflowX: 'visible',
    padding: '$0',
    scrollPadding: '$0',
  },
})

const Step = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$32',
  alignItems: 'flex-start',
  minWidth: '75vw',
  borderRadius: '$rBoxes',
  border: '1px solid #2D2D2D',
  padding: '$30',
  scrollSnapAlign: 'start',

  '@lg': {
    minWidth: 'auto',
  },
})

const StepNumber = styled('div', {
  display: 'flex',
  width: '80px',
  height: '80px',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '40px',
  border: '1px solid $white',
  projectFont: 'ui02',
  color: '$white',
  textAlign: 'center',
  lineHeight: '120%',
})

const StepTitle = styled('h3', {
  projectFont: 'heading07',
  color: '$white',
  fontSize: '24px',
  fontWeight: 450,
  letterSpacing: '-0.24px',
})

const StepContent = styled('div', {
  projectFont: 'heading08',
  color: '$gray300',
  fontWeight: 450,
  lineHeight: '120%',
  letterSpacing: '-0.18px',

  '& p': {
    margin: 0,
    padding: 0,
  },

  '& p + p': {
    marginTop: '22px',
  },
})

export const SectionSteps: FC<SectionStepsProps> = ({
  title,
  steps,
  spacing,
  topBorder,
}) => {
  if (!steps || steps.length === 0 || !title) return null

  return (
    <SectionWrapper type={spacing || 'DEFAULT'} topBorder={topBorder}>
      <Container>
        <Title>{title}</Title>
        <StepsScrollContainer>
          {steps.map((step, index) => (
            <Step key={step._key}>
              <StepNumber>{String(index + 1).padStart(2, '0')}</StepNumber>
              {step.title && <StepTitle>{step.title}</StepTitle>}
              {step.content && (
                <StepContent>
                  <PortableText value={step.content} appearance="plain" />
                </StepContent>
              )}
            </Step>
          ))}
        </StepsScrollContainer>
      </Container>
    </SectionWrapper>
  )
}
